// Shared editor for a single time.entries row (WI-11825).
//
// Used by both the desktop TimePage and the mobile MobileTimePage. Renders as
// a right-side drawer on desktop and a bottom-sheet on mobile (via the `mode`
// prop). Exposes worked hours, billable hours, notes, and invoice text. The
// project / phase / activity context is shown read-only — to change those,
// delete and re-add via the picker.
//
// Saving uses PATCH /time/entries/{id}. Deleting uses DELETE /time/entries/{id}.
// The form is read-only when the parent passes a locked weekStatus.

import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { X, Trash2 } from 'lucide-react'
import { api } from '@/lib/api/client'
import type { TimeEntry, WeekStatusValue } from '@/types/api'

const MAX_TEXT = 500

interface Props {
  entry:        TimeEntry | null         // null = closed
  weekStatus?:  WeekStatusValue | null   // for locked-week banner + read-only
  mode:         'drawer' | 'sheet'
  onClose:      () => void
  onSaved?:     () => void               // parent invalidates queries
}

export default function TimeEntryEditor({ entry, weekStatus, mode, onClose, onSaved }: Props) {
  const queryClient = useQueryClient()
  const locked = weekStatus === 'submitted' || weekStatus === 'approved'

  const [worked,   setWorked]   = useState('')
  const [billable, setBillable] = useState('')
  const [notes,    setNotes]    = useState('')
  const [invoice,  setInvoice]  = useState('')
  const [confirmDelete, setConfirmDelete] = useState(false)

  // Reset form when a new entry opens.
  useEffect(() => {
    if (!entry) return
    setWorked  (String(entry.workedHrs ?? 0))
    setBillable(String(entry.billableHrs ?? 0))
    setNotes   (entry.notes        ?? '')
    setInvoice (entry.invoiceText  ?? '')
    setConfirmDelete(false)
  }, [entry?.id])

  const save = useMutation({
    mutationFn: () => api.patch<{ id: number }>(`/time/entries/${entry?.id}`, {
      workedHrs:   Number(worked),
      billableHrs: Number(billable),
      notes:       notes,
      invoiceText: invoice,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time', 'entries'] })
      onSaved?.()
      onClose()
    },
  })

  const remove = useMutation({
    mutationFn: () => api.delete<unknown>(`/time/entries/${entry?.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time', 'entries'] })
      onSaved?.()
      onClose()
    },
  })

  if (!entry) return null

  const dirty =
    Number(worked)   !== entry.workedHrs   ||
    Number(billable) !== entry.billableHrs ||
    (notes   || '') !== (entry.notes       ?? '') ||
    (invoice || '') !== (entry.invoiceText ?? '')

  const body = (
    <>
      <header className={mode === 'drawer' ? 'drawer-head' : 'te-sheet-head'}>
        <div>
          <div className="te-context-eyebrow">{entry.customerName ?? '—'}</div>
          <div className="te-context-title">
            {entry.projectName ?? <em>No project</em>}
          </div>
          <div className="te-context-sub">
            {[entry.phaseName, entry.activityName].filter(Boolean).join(' · ') || '—'}
            {' · '}{entry.entryDate}
          </div>
        </div>
        <button onClick={onClose} className="icon-btn" aria-label="Close">
          <X size={18} />
        </button>
      </header>

      <div className={mode === 'drawer' ? 'drawer-body' : 'te-sheet-body'}>
        {locked && (
          <div className="te-locked-banner">
            This week is {weekStatus}. Edits and deletes are disabled.
          </div>
        )}

        <div className="te-row">
          <label className="te-label" htmlFor="te-worked">Worked hours</label>
          <input
            id="te-worked"
            className="input mono"
            type="number" min={0} step={0.25}
            value={worked}
            onChange={e => setWorked(e.target.value)}
            disabled={locked}
          />
        </div>

        <div className="te-row">
          <label className="te-label" htmlFor="te-billable">Billable hours</label>
          <input
            id="te-billable"
            className="input mono"
            type="number" min={0} step={0.25}
            value={billable}
            onChange={e => setBillable(e.target.value)}
            disabled={locked}
          />
          {Number(billable) > Number(worked) && (
            <div className="te-hint warn">Billable exceeds worked — usually a mistake.</div>
          )}
        </div>

        <div className="te-row">
          <label className="te-label" htmlFor="te-invoice">Invoice text</label>
          <textarea
            id="te-invoice"
            className="input"
            rows={3}
            placeholder="Goes on the customer invoice line"
            value={invoice}
            onChange={e => setInvoice(e.target.value.slice(0, MAX_TEXT))}
            disabled={locked}
          />
          <div className={`te-count ${invoice.length > MAX_TEXT - 50 ? 'warn' : ''}`}>
            {invoice.length} / {MAX_TEXT}
          </div>
        </div>

        <div className="te-row">
          <label className="te-label" htmlFor="te-notes">Notes</label>
          <textarea
            id="te-notes"
            className="input"
            rows={3}
            placeholder="Internal notes — not shown on invoice"
            value={notes}
            onChange={e => setNotes(e.target.value.slice(0, MAX_TEXT))}
            disabled={locked}
          />
          <div className={`te-count ${notes.length > MAX_TEXT - 50 ? 'warn' : ''}`}>
            {notes.length} / {MAX_TEXT}
          </div>
        </div>

        {save.isError && (
          <div className="te-error">Failed to save. {(save.error as Error).message}</div>
        )}
        {remove.isError && (
          <div className="te-error">Failed to delete. {(remove.error as Error).message}</div>
        )}
      </div>

      <footer className={mode === 'drawer' ? 'drawer-foot' : 'te-sheet-foot'}>
        {!locked && (confirmDelete ? (
          <>
            <span style={{ flex: 1, fontSize: 12.5, color: 'var(--ns-text-muted)' }}>
              Delete this entry?
            </span>
            <button className="btn btn-ghost" onClick={() => setConfirmDelete(false)}>
              Cancel
            </button>
            <button className="btn btn-danger" onClick={() => remove.mutate()} disabled={remove.isPending}>
              {remove.isPending ? 'Deleting…' : 'Delete'}
            </button>
          </>
        ) : (
          <>
            <button
              className="btn btn-ghost te-delete"
              onClick={() => setConfirmDelete(true)}
              title="Delete this entry"
            >
              <Trash2 size={14} />
            </button>
            <div style={{ flex: 1 }} />
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button
              className="btn btn-brand"
              onClick={() => save.mutate()}
              disabled={!dirty || save.isPending || Number(worked) <= 0}
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
          </>
        ))}
        {locked && <button className="btn btn-ghost" onClick={onClose}>Close</button>}
      </footer>
    </>
  )

  if (mode === 'drawer') {
    return (
      <>
        <div className="drawer-backdrop" onClick={onClose} />
        <div className="drawer te-drawer">{body}</div>
      </>
    )
  }
  return (
    <>
      <div className="bottom-sheet-backdrop" onClick={onClose} />
      <div className="bottom-sheet te-sheet">{body}</div>
    </>
  )
}
